import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Mail, Lock, User, CheckCircle, AlertCircle, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import SEO from '@/components/SEO';
import { trackLead, trackCompleteRegistration } from '@/utils/metaPixel';

// Use window.location.origin to ensure we always call the correct backend
const API = `${window.location.origin}/api`;
const LOGO_URL = "/images/dea-wordmark.png"; // Full "Dea" logo for auth pages

export default function Signup() {
  const [searchParams] = useSearchParams();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [invitationCode, setInvitationCode] = useState('');
  const [codeValid, setCodeValid] = useState(null); // null, true, false
  const [codeMessage, setCodeMessage] = useState('');
  const { signup } = useAuth();
  const navigate = useNavigate();

  // Check for invitation code in URL
  useEffect(() => {
    const code = searchParams.get('code');
    if (code) {
      setInvitationCode(code);
      validateCode(code);
    }
  }, [searchParams]);

  const validateCode = async (code) => {
    if (!code) {
      setCodeValid(null);
      setCodeMessage('');
      return;
    }
    
    try {
      const response = await axios.get(`${API}/invitation/validate/${code}`);
      setCodeValid(response.data.valid);
      setCodeMessage(response.data.message);
    } catch (error) {
      setCodeValid(false);
      setCodeMessage('Unable to validate code');
    }
  };

  const handleCodeChange = (e) => {
    const code = e.target.value.toUpperCase();
    setInvitationCode(code);
    if (code.length >= 8) {
      validateCode(code);
    } else {
      setCodeValid(null);
      setCodeMessage('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    // REQUIRED: Invitation code must be provided and valid
    if (!invitationCode) {
      toast.error('Invitation code is required. Please purchase access to get a code.');
      return;
    }
    if (!codeValid) {
      toast.error('Please enter a valid invitation code');
      return;
    }
    
    // Track Lead event when user attempts signup
    trackLead({ content_name: 'Dea Signup Form' });
    
    setLoading(true);
    try {
      await signup(name, email, password, invitationCode);
      
      // Track CompleteRegistration on successful signup
      trackCompleteRegistration({ 
        content_name: 'Dea Account',
        status: 'registered'
      });
      
      toast.success('Welcome to Dea, sis!');
      navigate('/chat');
    } catch (error) {
      const message = error.response?.data?.detail || 'Signup failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO 
        title="Create Your Account"
        description="Join Dea and start your 90-day transformation journey with personalized AI coaching from Dea."
      />
      <div className="min-h-screen bg-[#FFFCF7] flex">
      {/* Left - Portrait Video */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-[#EAD8C2]/30 overflow-hidden">
        <video
          id="signup-video"
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          playsInline
          onEnded={() => {
            const btn = document.getElementById('signup-play-btn');
            const video = document.getElementById('signup-video');
            if (btn) btn.style.display = 'flex';
            if (video) video.muted = true;
          }}
        >
          <source src="https://storage.googleapis.com/msgsndr/RVXeSVbF3U7E56SwQumk/media/698e4aeaa44964c4d7bbbfe9.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-t from-[#726158]/60 to-transparent" />
        
        {/* Play button overlay */}
        <button
          id="signup-play-btn"
          onClick={() => {
            const video = document.getElementById('signup-video');
            const btn = document.getElementById('signup-play-btn');
            if (video) {
              video.muted = false;
              video.currentTime = 0;
              video.play();
              if (btn) btn.style.display = 'none';
            }
          }}
          className="absolute inset-0 flex items-center justify-center z-10 group"
        >
          <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:bg-white/30 transition-all group-hover:scale-110">
            <Play className="h-10 w-10 text-white ml-1" />
          </div>
        </button>
        
        <div className="absolute bottom-12 left-12 right-12 z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <p className="font-serif font-brand-accent text-3xl text-[#FFFCF7] mb-4">Your transformation starts now...</p>
            <p className="text-[#FFFCF7]/90 text-lg max-w-md">
              "In 90 days, you're looking back and saying: Girl, I did it."
            </p>
          </motion.div>
        </div>
      </div>

      {/* Right - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#FFFCF7]">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="w-full max-w-md space-y-8"
        >
          <div className="text-center lg:text-left">
            <Link to="/" className="inline-block mb-8">
              <img src={LOGO_URL} alt="Dea" className="h-16 w-auto object-contain" />
            </Link>
            <h1 className="font-serif text-3xl text-[#726158] mb-2">Start Your Glow Up</h1>
            <p className="text-[#AA9283]">Let's build the business and life you're dreaming of.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Invitation Code Field */}
            <div className="space-y-2">
              <Label htmlFor="code" className="text-[#AA9283]">Invitation Code</Label>
              <div className="relative">
                <Input
                  id="code"
                  type="text"
                  placeholder="AGPT-XXXXXXXXXXXX"
                  value={invitationCode}
                  onChange={handleCodeChange}
                  className={`input-light font-mono tracking-wider ${
                    codeValid === true ? 'border-[#87A6A7] bg-[#87A6A7]/5' : 
                    codeValid === false ? 'border-red-400 bg-red-50' : ''
                  }`}
                  data-testid="signup-code-input"
                />
                {codeValid !== null && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {codeValid ? (
                      <CheckCircle className="h-5 w-5 text-[#87A6A7]" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-red-500" />
                    )}
                  </div>
                )}
              </div>
              {codeMessage && (
                <p className={`text-xs ${codeValid ? 'text-[#87A6A7]' : 'text-red-500'}`}>
                  {codeMessage}
                </p>
              )}
              {!invitationCode && (
                <p className="text-xs text-[#B1897C]">
                  Don't have a code? <Link to="/checkout?plan=monthly" className="text-[#87A6A7] hover:underline">Purchase access</Link>
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="name" className="text-[#AA9283]">Your Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#B1897C]" />
                <Input
                  id="name"
                  type="text"
                  placeholder="What should Dea call you?"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input-light pl-10"
                  data-testid="signup-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#AA9283]">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#B1897C]" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-light pl-10"
                  data-testid="signup-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#AA9283]">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#B1897C]" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-light pl-10 pr-10"
                  data-testid="signup-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#B1897C] hover:text-[#AA9283] transition-colors"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full btn-gold py-6"
              disabled={loading}
              data-testid="signup-submit-btn"
            >
              {loading ? <span className="spinner" /> : 'Create Account'}
            </Button>
          </form>

          <p className="text-center text-[#AA9283]">
            Already have an account?{' '}
            <Link to="/login" className="text-[#87A6A7] hover:text-[#CAAB84] font-medium">
              Sign In
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
    </>
  );
}
